import React, { type AllHTMLAttributes, type PropsWithChildren } from 'react';
import cx from 'classnames';
import {
  type NumericGap,
  type SpacingToken,
} from '@mc/wink/helpers/spacing-map';
import stylesheet from './StackLayout.less';

export type StackLayoutProps = PropsWithChildren<{
  /** The node used for the root element of StackLayout. */
  as?: React.ElementType;
  /** Controls spacing between children. Uses component spacing. */
  gap?: NumericGap | SpacingToken;
}> &
  AllHTMLAttributes<unknown>;
//We are not sure what value of `as` will be passed in here,
//so I decided to use AllHTMLAttributes<unknown> here.

/**
 * `StackLayout` stacks **block-level** elements.
 *
 * All children are stacked in a single column. Inline elements get stretched
 * and become blocks. If you wish to avoid this, wrap inline elements with
 * another element, like a `<div>`.
 */
const StackLayout = React.forwardRef<HTMLElement, StackLayoutProps>(
  function StackLayout(
    { as: Component = 'div', children, className, gap = 4, ...props },
    forwardedRef,
  ) {
    // Implements the `StackLayout` on every-layout.dev:
    // https://every-layout.dev/layouts/stack
    //
    // Note that there is a bug in every-layout's version, which uses CSS
    // variables: Nested `StackLayout`s override the CSS variable of its parent,
    // causing unwanted `gap` values.
    return (
      <Component
        className={cx(stylesheet.stack, stylesheet[`spacing${gap}`], className)}
        ref={forwardedRef}
        {...props}
      >
        {children}
      </Component>
    );
  },
);

export default StackLayout;
